import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AppProvider, useApp } from "@/context/AppContext";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/components/Dashboard";
import ReuploadBanner from "@/components/ReuploadBanner";
import ResumePanel from "@/components/ResumePanel";
import SearchPanel from "@/components/SearchPanel";
import JobBoard from "@/components/JobBoard";
import JobDrawer from "@/components/JobDrawer";
import KanbanBoard from "@/components/KanbanBoard";
import GeneratorPanel from "@/components/GeneratorPanel";
import AlertsPanel from "@/components/AlertsPanel";
import PersonaSelector from "@/components/PersonaSelector";
import GuidePage from "@/components/GuidePage";
import WatchlistPanel from "@/components/WatchlistPanel";
// SettingsPanel and SystemHealth are admin-only; moved to AdminPanel tabs (Gate 2)
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import WaitlistPage from "@/pages/WaitlistPage";

const VIEW_KEY = 'sankhya_active_view';
const VALID_VIEWS = new Set(["dashboard","resume","search","results","pipeline","generator","radar","watchlist","guide"]);

function AppContent() {
  const [view, setView] = useState(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return stored && VALID_VIEWS.has(stored) ? stored : "dashboard";
  });
  const {
    selectedJob, setSelectedJob, fetchProfile, fetchStats,
    personas, activePersonaId, setActivePersonaId, showPersonaSelector, setShowPersonaSelector,
    personasReady,
  } = useApp();

  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    if (!personasReady) return;
    fetchProfile();
    fetchStats();
  }, [fetchProfile, fetchStats, activePersonaId, personasReady]);

  // Show selector on first load if no active persona
  useEffect(() => {
    if (!personas || personas.length === 0) return; // Wait for API to load
    const noActive = !activePersonaId || !personas.find(p => p.id === activePersonaId);
    if (noActive && personas.length === 1) {
      setActivePersonaId(personas[0].id);
      setShowSelector(false);
    } else if (noActive && personas.length > 1) {
      setShowSelector(true);
    }
  }, [personas, activePersonaId, setActivePersonaId]);

  useEffect(() => {
    if (showPersonaSelector) {
      setShowSelector(true);
      setShowPersonaSelector(false);
    }
  }, [showPersonaSelector, setShowPersonaSelector]);

  const navigate = (newView) => {
    setView(newView);
    localStorage.setItem(VIEW_KEY, newView);
    setSelectedJob(null);
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-root">
      {showSelector && (
        <PersonaSelector onDismiss={() => setShowSelector(false)} />
      )}

      <Sidebar view={view} setView={navigate} onSwitchPersona={() => setShowSelector(true)} />

      <ReuploadBanner setView={navigate} />

      <main className="jp-main">
        {view === "dashboard" && <Dashboard setView={navigate} />}
        {view === "resume"    && <ResumePanel />}
        {view === "search"    && <SearchPanel />}
        {view === "results"   && <JobBoard />}
        {view === "pipeline"  && <KanbanBoard />}
        {view === "generator" && <GeneratorPanel />}
        {view === "radar"     && <AlertsPanel />}
        {view === "watchlist" && <WatchlistPanel />}
        {view === "guide"     && <GuidePage />}
      </main>

      <Sheet open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <SheetContent side="right" className="w-[460px] sm:max-w-[460px] bg-[#121217] border-l border-[#242430] p-0 overflow-y-auto">
          {selectedJob && <JobDrawer />}
        </SheetContent>
      </Sheet>

      <Toaster position="bottom-right" />
    </div>
  );
}

import AdminPanel from "@/components/AdminPanel";
import ForceResetModal from "@/components/ForceResetModal";
import PersonaSetupPage from "@/pages/PersonaSetupPage";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, token, loading, isAdmin } = useApp();
  
  if (loading.auth) return <div className="h-screen bg-[#09090b] flex items-center justify-center text-white">Loading...</div>;
  if (!token) return <Navigate to="/auth" replace />;

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user?.must_change_password) {
    return (
      <div className="relative h-screen">
        <div className="blur-sm h-full pointer-events-none">
          {children}
        </div>
        <ForceResetModal />
      </div>
    );
  }

  return children;
}

function App() {
  return (
    <AppProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<WaitlistPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
        <Route path="/dashboard/persona-setup" element={<ProtectedRoute><PersonaSetupPage /></ProtectedRoute>} />
        {/* /admin hard-refreshes to /admin/health; /admin/:tab deep-links to a tab */}
        <Route path="/admin" element={<Navigate to="/admin/health" replace />} />
        <Route path="/admin/:tab" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
        {/* Fallback to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppProvider>
  );
}

export default App;
